import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, NavLink } from "react-router-dom";
import Papa from "papaparse";
import Documentation from "./Documentation.js";
import Categories from "./Categories";
import "./App.css";
import {
  FaSearchPlus,
  FaSearchMinus,
  FaSyncAlt,
  FaDownload,
  FaCopy,
  FaChevronDown,
  FaTimes,
  FaCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

function App() {
  return (
    <Router>
      <div className="App">
        <nav className="nav-bar">
          <NavLink to="/" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Database
          </NavLink>
          <NavLink to="/docs" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Docs
          </NavLink>
        </nav>

        <Routes>
          <Route path="/" element={<DatabasePage />} />
          <Route path="/docs" element={<Categories />} />
        </Routes>
      </div>
    </Router>
  );
}

// New component to hold both tables
function DatabasePage() {
  const [activeDatabase, setActiveDatabase] = useState('all-time');
  const [showArchiveGrid, setShowArchiveGrid] = useState(false);
  const [selectedArchiveFile, setSelectedArchiveFile] = useState(null);
  
  const archiveFiles = [
    { name: 'January 16th', path: 'https://raw.githubusercontent.com/abysslives/openwallets/refs/heads/main/25jan16' },
    { name: 'January 17th', path: 'https://raw.githubusercontent.com/abysslives/openwallets/refs/heads/main/25jan17' },
    { name: 'January 23rd', path: 'https://raw.githubusercontent.com/abysslives/openwallets/refs/heads/main/25jan23' },
    // Add more archive files here
  ];

  const getCsvUrl = () => {
    if (activeDatabase === 'all-time') {
      return "https://raw.githubusercontent.com/abysslives/openwallets/refs/heads/main/data.csv";
    } else if (activeDatabase === '24-hour') {
      return "https://raw.githubusercontent.com/abysslives/openwallets/refs/heads/main/24hour";
    } else {
      return selectedArchiveFile || archiveFiles[0].path;
    }
  };

  return (
    <div className="database-container">
      <div className="database-selector">
        <button 
          className={`database-title ${activeDatabase === '24-hour' ? 'active' : ''}`}
          onClick={() => {
            setActiveDatabase('24-hour');
            setShowArchiveGrid(false);
          }}
        >
          24 Hour Upload
        </button>
        <button 
          className={`database-title ${activeDatabase === 'all-time' ? 'active' : ''}`}
          onClick={() => {
            setActiveDatabase('all-time');
            setShowArchiveGrid(false);
          }}
        >
          All Time Database
        </button>
        <button 
          className={`database-title ${activeDatabase === 'archive' ? 'active' : ''}`}
          onClick={() => {
            setActiveDatabase('archive');
            setShowArchiveGrid(true);
          }}
        >
          Archive
        </button>
      </div>

      {activeDatabase === 'archive' && showArchiveGrid ? (
        <div className="archive-grid">
          {archiveFiles.map((file, index) => (
            <button
              key={index}
              className="archive-file-button"
              onClick={() => {
                setSelectedArchiveFile(file.path);
                setShowArchiveGrid(false);
              }}
            >
              {file.name}
            </button>
          ))}
        </div>
      ) : (
        <>
          {activeDatabase === 'archive' && !showArchiveGrid && (
            <button 
              className="change-file-button"
              onClick={() => setShowArchiveGrid(true)}
            >
              Change File
            </button>
          )}
          <Database csvUrl={getCsvUrl()} key={activeDatabase + selectedArchiveFile} />
        </>
      )}
    </div>
  );
}

function Database({ csvUrl }) {
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [copySuccess, setCopySuccess] = useState("");
  const [filters, setFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 250;

  // Parse the CSV file on initial load
  useEffect(() => {
    fetch(csvUrl)
      .then(response => response.text())
      .then(data => {
        Papa.parse(data, {
          header: true,
          complete: (result) => {
            setTableData(result.data);
            if (result.data.length > 0) {
              const headersList = Object.keys(result.data[0]);
              setHeaders(headersList);
              const initialFilters = {};
              const initialHiddenColumns = {};
              headersList.slice(1).forEach((header) => {
                initialFilters[header] = {
                  enabled: false,
                  ...(header === "address" || header === "tags"
                    ? { keyword: "" }
                    : { min: "", max: "" }),
                };
                initialHiddenColumns[header] = false;
              });
              setFilters(initialFilters);
              setHiddenColumns(initialHiddenColumns);
            }
          },
        });
      })
      .catch(error => console.error('Error fetching CSV:', error));
  }, [csvUrl]);

  // Filtered Data Logic
  const filteredData = useMemo(() => {
    const filtered = tableData.filter((row) => {
      return Object.keys(filters).every((column) => {
        const filter = filters[column];
        if (!filter.enabled) return true;

        if (column === "address" || column === "tags") {
          return row[column]?.toLowerCase().includes(filter.keyword.toLowerCase());
        }

        const value = parseFloat(row[column]);
        const min = filter.min === "" ? -Infinity : parseFloat(filter.min);
        const max = filter.max === "" ? Infinity : parseFloat(filter.max);
        return !isNaN(value) && value >= min && value <= max;
      });
    });

    // Reset to page 1 if current page would be empty
    const totalPages = Math.ceil(filtered.length / rowsPerPage);
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }

    return filtered;
  }, [filters, tableData, currentPage, rowsPerPage]);

  // Handle Filter Change
  const handleFilterChange = useCallback((column, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [column]: {
        ...prev[column],
        [field]: value,
      },
    }));
  }, []);

  // Clear Filter
  const clearFilter = useCallback((column) => {
    setFilters((prev) => ({
      ...prev,
      [column]: {
        enabled: false,
        ...(column === "address" || column === "tags"
          ? { keyword: "" }
          : { min: "", max: "" }),
      },
    }));
  }, []);

  // Toggle Filter Dropdown
  const toggleFilterDropdown = (header) => {
    setActiveFilter(activeFilter === header ? null : header);
  };

  // Toggle Column Visibility
  const toggleColumnVisibility = (header) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [header]: !prev[header],
    }));
  };

  // Reset CSV
  const resetCSV = () => {
    setFilters((prev) => {
      const resetFilters = {};
      Object.keys(prev).forEach((key) => {
        resetFilters[key] = { enabled: false, min: "", max: "" };
      });
      return resetFilters;
    });
    setHiddenColumns((prev) => {
      const resetHidden = {};
      Object.keys(prev).forEach((key) => {
        resetHidden[key] = false;
      });
      return resetHidden;
    });
  };

  // Unhide All Columns
  const unhideAllColumns = () => {
    setHiddenColumns((prev) => {
      const resetHidden = {};
      Object.keys(prev).forEach((key) => {
        resetHidden[key] = false;
      });
      return resetHidden;
    });
  };

  // Copy CSV Text
  const copyCSVText = () => {
    const csvContent = Papa.unparse(filteredData);
    navigator.clipboard.writeText(csvContent)
      .then(() => {
        setCopySuccess("Copied!");
        setTimeout(() => setCopySuccess(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text:", err);
        setCopySuccess("Failed to copy");
      });
  };

  // Download CSV
  const downloadCSV = () => {
    const csvContent = Papa.unparse(filteredData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "filtered_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add pagination controls
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Add this after filteredData calculation
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Add this new handler in the Database component
  const handlePageInputChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      setCurrentPage(value);
    }
  };

  return (
    <div className="App">
      <div className="buttons-container">
        <button className="custom-button" onClick={copyCSVText}>
          <FaCopy />
          Copy
        </button>
        <button className="custom-button" onClick={downloadCSV}>
          <FaDownload />
          Download
        </button>
        <button onClick={resetCSV} className="custom-button">
          Reset Table Contents
        </button>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {headers.map((header, index) => {
                  if (hiddenColumns[header]) return null;
                  
                  const isFirstColumn = index === 0; // Check if it's the first column
                  return (
                    <th 
                      key={index} 
                      className={`header-cell ${isFirstColumn ? 'fixed-column' : ''}`}
                    >
                      <span className="header-text">{header}</span>
                      {header !== "date reviewed [m/d/y]" && (
                        <div className="header-filter">
                          <button
                            className={`filter-toggle ${filters[header]?.enabled ? "active" : ""}`}
                            onClick={() => toggleFilterDropdown(header)}
                          >
                            <FaChevronDown />
                          </button>
                          {activeFilter === header && (
                            <div className="filter-dropdown">
                              <div className="filter-header">
                                <button
                                  className={`toggle-dot ${filters[header]?.enabled ? "active" : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFilterChange(header, "enabled", !filters[header]?.enabled);
                                  }}
                                >
                                  <FaCircle />
                                </button>
                                <span>Enable Filter</span>
                              </div>

                              {header === "address" || header === "tags" ? (
                                <div className="filter-inputs">
                                  <input
                                    type="text"
                                    placeholder="Enter keyword"
                                    value={filters[header]?.keyword || ""}
                                    onChange={(e) => handleFilterChange(header, "keyword", e.target.value)}
                                    className="filter-input"
                                  />
                                </div>
                              ) : (
                                <div className="filter-inputs">
                                  <input
                                    type="number"
                                    placeholder="Min"
                                    value={filters[header]?.min}
                                    onChange={(e) => handleFilterChange(header, "min", e.target.value)}
                                    className="filter-input"
                                  />
                                  <input
                                    type="number"
                                    placeholder="Max"
                                    value={filters[header]?.max}
                                    onChange={(e) => handleFilterChange(header, "max", e.target.value)}
                                    className="filter-input"
                                  />
                                </div>
                              )}

                              <button
                                className="clear-filter"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearFilter(header);
                                }}
                              >
                                <FaTimes /> Clear Filter
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((header, colIndex) => {
                    if (hiddenColumns[header]) return null;
                    
                    let cellStyle = {};
                    const value = row[header];
                    
                    if (header.toLowerCase().includes('profit') || header.toLowerCase().includes('pnl')) {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        if (numValue < 0) {
                          cellStyle.color = '#ff69b4'; // Hot pink for negative values
                        } else if (numValue > 0) {
                          cellStyle.color = '#00ffff'; // Cyan for positive values
                        }
                      }
                    }
                    
                    return (
                      <td 
                        key={colIndex} 
                        style={cellStyle}
                        className={header === 'address' ? 'fixed-column' : ''}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination-controls">
        <button 
          className="custom-button"
          onClick={() => handlePageChange(1)} 
          disabled={currentPage === 1}
        >
          First
        </button>
        <button 
          className="custom-button"
          onClick={() => handlePageChange(currentPage - 1)} 
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="page-info">
          Page <input 
            type="number"
            value={currentPage}
            onChange={handlePageInputChange}
            min="1"
            max={totalPages}
            className="page-input"
          /> of {totalPages}
        </span>
        <button 
          className="custom-button"
          onClick={() => handlePageChange(currentPage + 1)} 
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button 
          className="custom-button"
          onClick={() => handlePageChange(totalPages)} 
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    </div>
  );
}

export default App;